import { Field } from '../../components/ui';
import type { OnboardingOption } from '../../lib/types';
import { BACKUP_TYPES, COMPANY_SIZES, TIMELINES } from './onboardingMeta';

/**
 * The wizard's questions, as four field groups.
 *
 * Shared because they are asked twice: once on the way in, stepped, and again
 * afterwards when somebody corrects an answer. Those two used to be the same
 * screen in the source app only because editing re-opened the wizard — which
 * also meant the edit form asked for the company name of a client that had
 * already been created. Splitting the groups out lets the wizard step through
 * them and the editor show them all at once, without two copies of the same
 * eighteen inputs drifting apart.
 */

export interface AnswerForm {
  companySize: string;
  address: string;
  city: string;
  region: string;
  postalCode: string;

  contactFirstName: string;
  contactLastName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  preferredContact: 'email' | 'phone' | 'either';

  services: string[];
  backupType: string;
  serviceNotes: string;

  userCount: string;
  currentProvider: string;
  operatingSystems: string[];
  platforms: string[];
  painPoints: string;
  timeline: string;
}

export const BLANK_ANSWERS: AnswerForm = {
  companySize: '',
  address: '',
  city: '',
  region: '',
  postalCode: '',
  contactFirstName: '',
  contactLastName: '',
  contactTitle: '',
  contactEmail: '',
  contactPhone: '',
  preferredContact: 'email',
  services: [],
  backupType: '',
  serviceNotes: '',
  userCount: '',
  currentProvider: '',
  operatingSystems: [],
  platforms: [],
  painPoints: '',
  timeline: '',
};

export const toggleValue = (list: string[], value: string) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

/** The answers as the API wants them: trimmed, and '' turned into null. */
export function answerPayload(form: AnswerForm) {
  const text = (v: string) => v.trim() || null;
  return {
    companySize: form.companySize || null,
    address: text(form.address),
    city: text(form.city),
    region: text(form.region),
    postalCode: text(form.postalCode),
    contactFirstName: text(form.contactFirstName),
    contactLastName: text(form.contactLastName),
    contactTitle: text(form.contactTitle),
    contactEmail: text(form.contactEmail),
    contactPhone: text(form.contactPhone),
    preferredContact: form.preferredContact,
    services: form.services,
    backupType: form.backupType || null,
    serviceNotes: text(form.serviceNotes),
    userCount: form.userCount.trim() === '' ? null : Number(form.userCount),
    currentProvider: text(form.currentProvider),
    operatingSystems: form.operatingSystems,
    platforms: form.platforms,
    painPoints: text(form.painPoints),
    timeline: form.timeline || null,
  };
}

type Setter = (patch: Partial<AnswerForm>) => void;

export function LocationFields({ form, set }: { form: AnswerForm; set: Setter }) {
  return (
    <>
      <div className="form-grid">
        <Field label="Company size">
          <select
            className="select"
            value={form.companySize}
            onChange={(e) => set({ companySize: e.target.value })}
          >
            <option value="">Not stated</option>
            {COMPANY_SIZES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Street address">
          <input
            className="input"
            value={form.address}
            onChange={(e) => set({ address: e.target.value })}
            placeholder="123 Main St"
          />
        </Field>
      </div>

      <div className="form-grid">
        <Field label="City">
          <input className="input" value={form.city} onChange={(e) => set({ city: e.target.value })} />
        </Field>
        <Field label="State / region">
          <input
            className="input"
            value={form.region}
            onChange={(e) => set({ region: e.target.value })}
          />
        </Field>
      </div>

      <Field label="Postcode">
        <input
          className="input"
          value={form.postalCode}
          onChange={(e) => set({ postalCode: e.target.value })}
        />
      </Field>
    </>
  );
}

export function ContactFields({
  form,
  set,
  autoFocus,
}: {
  form: AnswerForm;
  set: Setter;
  autoFocus?: boolean;
}) {
  return (
    <>
      <div className="form-grid">
        <Field label="First name">
          <input
            className="input"
            value={form.contactFirstName}
            onChange={(e) => set({ contactFirstName: e.target.value })}
            placeholder="Jane"
            autoFocus={autoFocus}
          />
        </Field>
        <Field label="Last name">
          <input
            className="input"
            value={form.contactLastName}
            onChange={(e) => set({ contactLastName: e.target.value })}
            placeholder="Smith"
          />
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Job title">
          <input
            className="input"
            value={form.contactTitle}
            onChange={(e) => set({ contactTitle: e.target.value })}
            placeholder="IT Manager"
          />
        </Field>
        <Field label="Preferred contact">
          <select
            className="select"
            value={form.preferredContact}
            onChange={(e) => set({ preferredContact: e.target.value as AnswerForm['preferredContact'] })}
          >
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="either">Either</option>
          </select>
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Business email" hint="Every onboarding email is composed to this address.">
          <input
            className="input"
            type="email"
            value={form.contactEmail}
            onChange={(e) => set({ contactEmail: e.target.value })}
            placeholder="jane@company.com"
          />
        </Field>
        <Field label="Phone">
          <input
            className="input"
            value={form.contactPhone}
            onChange={(e) => set({ contactPhone: e.target.value })}
            placeholder="(555) 000-0000"
          />
        </Field>
      </div>
    </>
  );
}

export function ServiceFields({
  form,
  set,
  options,
}: {
  form: AnswerForm;
  set: Setter;
  options: OnboardingOption[];
}) {
  const services = options.filter((o) => o.Kind === 'service');
  return (
    <>
      <div className="onb-picks">
        {services.map((s) => {
          const on = form.services.includes(s.Code);
          return (
            <label key={s.Code} className={`onb-pick${on ? ' on' : ''}`}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => set({ services: toggleValue(form.services, s.Code) })}
              />
              <span className="box" aria-hidden="true">
                {on ? '✓' : ''}
              </span>
              <span>
                <span className="name">{s.Name}</span>
                {s.Description && <span className="desc">{s.Description}</span>}
              </span>
            </label>
          );
        })}
      </div>

      {/* The one follow-up question in the whole form. It appears only when
          backup is bought, because asking everybody which kind of backup they
          want when they have not bought one is how a form gets abandoned. */}
      {form.services.includes('backup') && (
        <div className="onb-followup">
          <p className="eyebrow">What kind of backup?</p>
          <div className="onb-picks tight mt-2">
            {BACKUP_TYPES.map((b) => (
              <label key={b.value} className={`onb-pick${form.backupType === b.value ? ' on' : ''}`}>
                <input
                  type="radio"
                  name="backupType"
                  checked={form.backupType === b.value}
                  onChange={() => set({ backupType: b.value })}
                />
                <span className="box round" aria-hidden="true">
                  {form.backupType === b.value ? '●' : ''}
                </span>
                <span>
                  <span className="name">{b.label}</span>
                  <span className="desc">{b.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <Field label="Notes" hint="Anything about the services that the tasks will not cover.">
        <textarea
          className="textarea"
          rows={3}
          value={form.serviceNotes}
          onChange={(e) => set({ serviceNotes: e.target.value })}
          placeholder="e.g. a legacy line-of-business system that has to be migrated first"
        />
      </Field>
    </>
  );
}

export function TechnicalFields({
  form,
  set,
  options,
}: {
  form: AnswerForm;
  set: Setter;
  options: OnboardingOption[];
}) {
  const operatingSystems = options.filter((o) => o.Kind === 'os');
  const platforms = options.filter((o) => o.Kind === 'platform');
  return (
    <>
      <div className="form-grid">
        <Field label="Users / endpoints">
          <input
            className="input"
            type="number"
            min={1}
            value={form.userCount}
            onChange={(e) => set({ userCount: e.target.value })}
            placeholder="25"
          />
        </Field>
        <Field label="Current IT provider">
          <input
            className="input"
            value={form.currentProvider}
            onChange={(e) => set({ currentProvider: e.target.value })}
            placeholder="In-house, or the incumbent's name"
          />
        </Field>
      </div>

      <Field label="Operating systems">
        <div className="onb-chips">
          {operatingSystems.map((o) => (
            <label
              key={o.Code}
              className={`onb-chip${form.operatingSystems.includes(o.Code) ? ' on' : ''}`}
            >
              <input
                type="checkbox"
                checked={form.operatingSystems.includes(o.Code)}
                onChange={() => set({ operatingSystems: toggleValue(form.operatingSystems, o.Code) })}
              />
              {o.Name}
            </label>
          ))}
        </div>
      </Field>

      <Field label="Cloud platforms">
        <div className="onb-chips">
          {platforms.map((o) => (
            <label key={o.Code} className={`onb-chip${form.platforms.includes(o.Code) ? ' on' : ''}`}>
              <input
                type="checkbox"
                checked={form.platforms.includes(o.Code)}
                onChange={() => set({ platforms: toggleValue(form.platforms, o.Code) })}
              />
              {o.Name}
            </label>
          ))}
        </div>
      </Field>

      <Field
        label="Pain points"
        hint="What made them move. Worth having when the first review comes round."
      >
        <textarea
          className="textarea"
          rows={3}
          value={form.painPoints}
          onChange={(e) => set({ painPoints: e.target.value })}
          placeholder="e.g. slow response from the incumbent, repeated outages, no backup testing"
        />
      </Field>

      <Field label="Start timeline">
        <select
          className="select"
          value={form.timeline}
          onChange={(e) => set({ timeline: e.target.value })}
        >
          <option value="">Not stated</option>
          {TIMELINES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </Field>
    </>
  );
}
